import { useMutation } from "@apollo/client/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { REGEXP_ONLY_DIGITS_AND_CHARS } from "input-otp"
import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { v4 as uuidv4 } from "uuid"
import * as z from "zod"
import { gql } from "~/__generated__"
import { useUiAccess } from "~/auth/use-ui-access"
import { useViewerMaybe } from "~/auth/use-viewer"
import { useNavigateAfterAuth } from "~/auth/utils"
import { useFormErrorHandling } from "~/common/error-handling"
import { loginPath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { EMAIL_TOKEN_AUTH_MUTATION } from "~/login/email-auth-screen"
import { updateCsrfTag } from "~/login/utils"
import { Button } from "~/ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/ui/form"
import { TextField } from "~/ui/forms/fields/text-field"
import { FormGeneralErrors } from "~/ui/forms/form-general-errors"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "~/ui/input-otp"

const signupFormSchema = z.object({
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  email: z.email({ message: "Invalid email address" }),
})

const otpFormSchema = z.object({
  otp: z.string().length(6, { message: "Your one-time password must be 6 characters." }),
})

type SignupFormValues = z.infer<typeof signupFormSchema>
type OtpFormValues = z.infer<typeof otpFormSchema>

const USER_SIGNUP_MUTATION = gql(/* GraphQL */ `
  mutation UserSignup($input: UserSignupInput!) {
    userSignup(input: $input) {
      success
    }
  }
`)

export const SignupScreen: React.FC = () => {
  useDocumentTitle("Create your account")

  const [userSignup, { loading: signupLoading }] = useMutation(USER_SIGNUP_MUTATION)
  const [emailTokenAuth, { loading: tokenAuthLoading }] = useMutation(EMAIL_TOKEN_AUTH_MUTATION)
  const [step, setStep] = useState<"details" | "checkEmail" | "otp">("details")
  const [email, setEmail] = useState("")
  const { navigateAfterAuth } = useNavigateAfterAuth()
  const {
    result: { refetch: viewerRefetch },
  } = useViewerMaybe()
  const { refetch: refetchUiAccess } = useUiAccess()

  const signupForm = useForm<SignupFormValues>({
    resolver: zodResolver(signupFormSchema),
    defaultValues: { firstName: "", lastName: "", email: "" },
  })

  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpFormSchema),
    defaultValues: { otp: "" },
  })

  const { onError: onSignupError } = useFormErrorHandling(signupForm)
  const { onError: onOtpError } = useFormErrorHandling(otpForm)

  const onSignupSubmit = async (values: SignupFormValues) => {
    const storedAuthCodes = JSON.parse(localStorage.getItem("authCodes") || "[]")
    let clientAuthCode: string
    if (storedAuthCodes.length === 0) {
      clientAuthCode = uuidv4()
      localStorage.setItem("authCodes", JSON.stringify([clientAuthCode]))
    } else {
      clientAuthCode = storedAuthCodes[storedAuthCodes.length - 1]
    }

    const { data, error } = await userSignup({
      onError: onSignupError,
      variables: {
        input: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          clientAuthCode,
        },
      },
    })

    if (error) return

    if (data?.userSignup.success) {
      setEmail(values.email)
      setStep("checkEmail")
      toast.success("Check your email", {
        description: "We sent you a magic link to sign in.",
      })
    }
  }

  const onOtpSubmit = async (values: OtpFormValues) => {
    const { data, error } = await emailTokenAuth({
      onError: onOtpError,
      variables: {
        input: {
          email,
          token: values.otp,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    })

    if (error) return

    if (data?.emailTokenUserAuth.success) {
      updateCsrfTag(data.emailTokenUserAuth.csrfToken)
      await viewerRefetch()
      await refetchUiAccess()
      toast.success("Welcome to Scout Radar!")
      navigateAfterAuth()
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="w-full max-w-md space-y-8 px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Create your account</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Get personalized auction alerts in your inbox
          </p>
        </div>

        {step === "details" && (
          <Form {...signupForm}>
            <form onSubmit={signupForm.handleSubmit(onSignupSubmit)} className="space-y-4">
              <FormGeneralErrors control={signupForm.control} />
              <div className="grid grid-cols-2 gap-4">
                <TextField
                  control={signupForm.control}
                  name="firstName"
                  label="First name"
                  placeholder="Jane"
                />
                <TextField
                  control={signupForm.control}
                  name="lastName"
                  label="Last name"
                  placeholder="Smith"
                />
              </div>
              <TextField
                control={signupForm.control}
                name="email"
                label="Email"
                placeholder="jane@example.com"
              />
              <Button type="submit" className="w-full" disabled={signupLoading}>
                {signupLoading ? "Sending..." : "Send magic link"}
              </Button>
            </form>
          </Form>
        )}

        {step === "checkEmail" && (
          <div className="space-y-4 text-center">
            <p className="text-muted-foreground">
              We sent a link to <strong>{email}</strong>. Click it to sign in, or enter the code
              below.
            </p>
            <Button onClick={() => setStep("otp")} variant="outline" className="w-full">
              Enter code manually
            </Button>
          </div>
        )}

        {step === "otp" && (
          <Form {...otpForm}>
            <form onSubmit={otpForm.handleSubmit(onOtpSubmit)} className="flex flex-col space-y-6">
              <FormGeneralErrors control={otpForm.control} />
              <FormField
                control={otpForm.control}
                name="otp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Enter your code</FormLabel>
                    <FormControl>
                      <InputOTP
                        maxLength={6}
                        {...field}
                        pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                        data-1p-ignore="true"
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={tokenAuthLoading}>
                {tokenAuthLoading ? "Verifying..." : "Continue"}
              </Button>
            </form>
          </Form>
        )}

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to={loginPath({})} className="text-foreground underline underline-offset-4">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
